import React, { useState, useRef, useEffect } from 'react';
import { FaCopy, FaTrash, FaArrowUp, FaArrowDown, FaFont, FaTextHeight, FaPalette, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify, FaBorderAll, FaBorderStyle, FaExpandAlt, FaCompressAlt, FaInfoCircle, FaBuilding, FaCamera, FaPhone, FaMapMarkerAlt, FaTools, FaImage, FaSync, FaUpload, FaPen, FaClipboardList, FaRulerCombined, FaFileAlt, FaPlus, FaFolderOpen, FaCog, FaLayerGroup } from 'react-icons/fa';
import { MdRoundedCorner } from 'react-icons/md';
import useEditorStore from '../../store/editorStore';
import { blockTypes, sectionTypes, fontOptions, textAlignOptions, imageFitOptions, headerLayoutOptions, galleryLayouts, backgroundGradients, frameStyles } from '../../config/constants';
import { SketchPicker } from 'react-color';
import GradientPicker, { AngleWheel } from './GradientPicker';
import './PropertiesPanel.css';

function PropertiesPanel() {
  const {
    selectedBlockId,
    selectedSectionId,
    getSelectedBlock,
    getSelectedSection,
    updateBlock,
    updateBlockStyles,
    updateSection,
    deleteBlock,
    deleteSection,
    duplicateBlock,
    bringToFront,
    sendToBack,
    addGalleryItem,
    updateGalleryItem,
    deleteGalleryItem,
    applyGalleryLayout,
    canvas,
    setCanvasBackground,
    setCanvasGradient,
    setGlobalFont,
    showPropertiesPanel,
    togglePropertiesPanel
  } = useEditorStore();

  const [showColorPicker, setShowColorPicker] = useState(null);
  const [orgInputMode, setOrgInputMode] = useState('select');
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [selectedGalleryItemId, setSelectedGalleryItemId] = useState(null);
  const [bgType, setBgType] = useState(canvas.backgroundGradient ? 'gradient' : 'solid'); // Initialize based on store
  const [panelWidth, setPanelWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const block = getSelectedBlock();
  const section = getSelectedSection();

  // Resize logic
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 300 && newWidth < 600) {
        setPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const commonProps = {
    style: { width: panelWidth }
  };

  const resizeHandle = (
    <div 
      className="resize-handle"
      onMouseDown={() => setIsResizing(true)}
      title="Drag to resize"
    />
  );

  // Mobile close button
  const mobileCloseBtn = (
    <button 
      className="mobile-close-btn"
      onClick={togglePropertiesPanel}
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        zIndex: 20,
        display: 'none' // Hidden by default, shown in CSS for mobile
      }}
    >
      ×
    </button>
  );

  // If section is selected, show section properties
  if (section && !block) {
    const handleSectionStyleChange = (key, value) => {
      const updatedStyles = { ...section.styles, [key]: value };
      updateSection(section.id, { styles: updatedStyles });
    };

    const handleLogoUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          handleSectionStyleChange('logoUrl', event.target.result);
          setShowLogoUpload(false);
        };
        reader.readAsDataURL(file);
      }
    };

    const toggleOrgInputMode = () => {
      const newMode = orgInputMode === 'select' ? 'text' : 'select';
      setOrgInputMode(newMode);
      handleSectionStyleChange('orgInputMode', newMode);
    };

    return (
      <div className={`properties-panel ${showPropertiesPanel ? 'mobile-visible' : ''}`} style={{ width: panelWidth }}>
        {resizeHandle}
        {mobileCloseBtn}
        <div className="properties-header">
          <h3>ตั้งค่าส่วน (Section)</h3>
          <div className="header-actions">
            <button 
              className="btn-icon" 
              onClick={() => setPanelWidth(panelWidth === 320 ? 450 : 320)}
              title={panelWidth > 350 ? "ย่อ" : "ขยาย"}
            >
              {panelWidth > 350 ? <FaCompressAlt /> : <FaExpandAlt />}
            </button>
          </div>
        </div>

        <div className="properties-content">
          <div className="property-section">
            <div className="section-title"><FaInfoCircle /> ข้อมูลทั่วไป</div>
            <div className="property-group">
              <label>ชนิด (Type)</label>
              <input type="text" value={section.type.toUpperCase()} disabled style={{background: '#f5f5f5', color: '#888'}} />
            </div>
          </div>

          {/* Header-specific properties */}
          {section.type === sectionTypes.HEADER && (
            <>
              <div className="property-section">
                <div className="section-title"><FaRulerCombined /> รูปแบบ (Layout)</div>
                <div className="property-group">
                  <div className="layout-options">
                    {headerLayoutOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`layout-option-btn ${(section.styles?.layout || 'logo-text') === option.value ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('layout', option.value)}
                        title={option.label}
                      >
                        <div className="layout-icon">{option.icon}</div>
                        <div className="layout-label">{option.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="property-section">
                <div className="section-title"><FaPen /> หัวข้อ</div>
                
                <div className="property-group">
                  <input
                    type="text"
                    value={section.styles?.title || ''}
                    onChange={(e) => handleSectionStyleChange('title', e.target.value)}
                    placeholder="หัวข้อโปสเตอร์"
                    className="text-input-large"
                  />
                </div>

                <div className="property-row">
                  <div className="property-group flex-1">
                    <label><FaFont /> Font</label>
                    <select
                      value={section.styles?.titleFont || 'Kanit'}
                      onChange={(e) => handleSectionStyleChange('titleFont', e.target.value)}
                    >
                      {fontOptions.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="property-group" style={{width: '100px'}}>
                    <label><FaTextHeight /> Size</label>
                    <input
                      type="number"
                      value={section.styles?.titleSize || 50}
                      onChange={(e) => handleSectionStyleChange('titleSize', parseInt(e.target.value))}
                      min="12"
                      max="200"
                    />
                  </div>
                </div>

                <div className="property-group">
                  <div className="color-group-inline">
                    <label><FaPalette /> สี</label>
                    <div className="color-input-wrapper">
                      <div
                        className="color-preview"
                        style={{ backgroundColor: section.styles?.titleColor || '#D041E6' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'titleColor' ? null : 'titleColor')}
                      />
                      <input
                        type="text"
                        value={section.styles?.titleColor || '#D041E6'}
                        onChange={(e) => handleSectionStyleChange('titleColor', e.target.value)}
                        placeholder="#D041E6"
                      />
                    </div>
                  </div>
                  {showColorPicker === 'titleColor' && (
                    <div className="color-picker-popover">
                    <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
                    <SketchPicker
                      color={section.styles?.titleColor || '#D041E6'}
                      onChange={(color) => handleSectionStyleChange('titleColor', color.hex)}
                    />
                  </div>
                )}
                </div>
              </div>

              <div className="property-section">
                <div className="section-title"><FaBuilding /> หน่วยงาน</div>
                
                <div className="property-group">
                  <label className="label-with-toggle">
                    <span>หน่วยงาน</span>
                    <button
                      className="toggle-mode-btn"
                      onClick={toggleOrgInputMode}
                      title={orgInputMode === 'select' ? 'เปลี่ยนเป็นกรอกเอง' : 'เปลี่ยนเป็นเลือก'}
                    >
                      {orgInputMode === 'select' ? <><FaPen /> กรอกเอง</> : <><FaClipboardList /> เลือก</>}
                    </button>
                  </label>
                  {orgInputMode === 'select' ? (
                    <select
                      value={section.styles?.organization || 'สำนักงานใหญ่'}
                      onChange={(e) => handleSectionStyleChange('organization', e.target.value)}
                    >
                      <option value="สำนักงานใหญ่">สำนักงานใหญ่</option>
                      <option value="กฟน. 1">กฟน. 1</option>
                      <option value="กฟฉ. 1">กฟฉ. 1</option>
                      <option value="กฟน. 2">กฟน. 2</option>
                      <option value="กฟฉ. 2">กฟฉ. 2</option>
                      <option value="กฟน. 3">กฟน. 3</option>
                      <option value="กฟฉ. 3">กฟฉ. 3</option>
                      <option value="กฟก. 1">กฟก. 1</option>
                      <option value="กฟต. 1">กฟต. 1</option>
                      <option value="กฟก. 2">กฟก. 2</option>
                      <option value="กฟต. 2">กฟต. 2</option>
                      <option value="กฟก. 3">กฟก. 3</option>
                      <option value="กฟต. 3">กฟต. 3</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={section.styles?.organization || ''}
                      onChange={(e) => handleSectionStyleChange('organization', e.target.value)}
                      placeholder="กรอกชื่อหน่วยงาน"
                    />
                  )}
                </div>

                <div className="property-row">
                  <div className="property-group flex-1">
                    <label><FaFont /> Font</label>
                    <select
                      value={section.styles?.orgFont || 'Sarabun'}
                      onChange={(e) => handleSectionStyleChange('orgFont', e.target.value)}
                    >
                      {fontOptions.map((font) => (
                      <option key={font.value} value={font.value}>
                        {font.label}
                      </option>
                    ))}
                    </select>
                  </div>

                  <div className="property-group" style={{width: '100px'}}>
                    <label><FaTextHeight /> Size</label>
                    <input
                      type="number"
                      value={section.styles?.orgSize || 36}
                      onChange={(e) => handleSectionStyleChange('orgSize', parseInt(e.target.value))}
                      min="12"
                      max="48"
                    />
                  </div>
                </div>

                <div className="property-group">
                  <div className="color-group-inline">
                    <label><FaPalette /> สี</label>
                    <div className="color-input-wrapper">
                      <div
                        className="color-preview"
                        style={{ backgroundColor: section.styles?.orgColor || '#414141' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'orgColor' ? null : 'orgColor')}
                      />
                      <input
                        type="text"
                        value={section.styles?.orgColor || '#414141'}
                        onChange={(e) => handleSectionStyleChange('orgColor', e.target.value)}
                        placeholder="#414141"
                      />
                    </div>
                  </div>
                  {showColorPicker === 'orgColor' && (
                    <div className="color-picker-popover">
                      <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
                      <SketchPicker
                        color={section.styles?.orgColor || '#414141'}
                        onChange={(color) => handleSectionStyleChange('orgColor', color.hex)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="property-group">
                <label>โลโก้</label>
                {section.styles?.logoUrl ? (
                  <div className="logo-preview-box">
                    <img 
                      src={section.styles.logoUrl} 
                      alt="Logo" 
                      className="logo-preview-img"
                    />
                    <div className="logo-actions">
                      <button
                        className="btn-change-logo"
                        onClick={() => setShowLogoUpload(true)}
                      >
                        <FaSync /> เปลี่ยนโลโก้
                      </button>
                      <button
                        className="btn-remove-logo"
                        onClick={() => handleSectionStyleChange('logoUrl', null)}
                      >
                        <FaTrash /> ลบ
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="logo-upload-box"
                    onClick={() => setShowLogoUpload(true)}
                  >
                    <div className="upload-icon"><FaUpload /></div>
                    <div className="upload-text">คลิกเพื่ออัปโหลดโลโก้</div>
                    <div className="upload-hint">PNG, JPG, SVG (แนะนำ 512×512px)</div>
                  </div>
                )}
                <input
                  ref={(input) => showLogoUpload && input?.click()}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                  id="logo-upload-input"
                />
              </div>
            </>
          )}

          {/* Gallery-specific properties */}
          {section.type === sectionTypes.GALLERY && (
            <>
              <div className="property-section">
                <div className="section-title"><FaImage /> Gallery</div>
                
                <div className="property-group">
                  <input
                    type="text"
                    value={section.styles?.galleryTitle || 'หัวข้อย่อย'}
                    onChange={(e) => handleSectionStyleChange('galleryTitle', e.target.value)}
                    placeholder="หัวข้อ Gallery"
                    className="text-input-large"
                  />
                </div>
              </div>

              <div className="property-section">
                <div className="section-title"><FaRulerCombined /> Layout</div>
                <div className="gallery-layouts-grid">
                  {galleryLayouts.map((layout) => (
                    <button
                      key={layout.id}
                      className={`gallery-layout-btn ${(section.styles?.selectedLayout || '') === layout.id ? 'active' : ''}`}
                      onClick={() => applyGalleryLayout(section.id, layout.id)}
                      title={layout.name}
                    >
                      <div className="layout-icon-large">{layout.icon}</div>
                      <div className="layout-name">{layout.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="property-section">
                <div className="section-title"><FaPalette /> รูปแบบ</div>
                
                <div className="property-row">
                  <div className="property-group flex-1">
                    <label><MdRoundedCorner /> Border Radius</label>
                    <input
                      type="number"
                      value={section.styles?.galleryBorderRadius || 0}
                      onChange={(e) => handleSectionStyleChange('galleryBorderRadius', parseInt(e.target.value))}
                      min="0"
                      max="50"
                    />
                  </div>

                  <div className="property-group" style={{width: '100px'}}>
                    <label><FaBorderStyle /> Width</label>
                    <input
                      type="number"
                      value={section.styles?.galleryBorderWidth || 0}
                      onChange={(e) => handleSectionStyleChange('galleryBorderWidth', parseInt(e.target.value))}
                      min="0"
                      max="20"
                    />
                  </div>
                </div>

                <div className="property-group">
                  <div className="color-group-inline">
                    <label><FaPalette /> Border</label>
                    <div className="color-input-wrapper">
                      <div
                        className="color-preview"
                        style={{ backgroundColor: section.styles?.galleryBorderColor || '#D041E6' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'galleryBorder' ? null : 'galleryBorder')}
                      />
                      <input
                        type="text"
                        value={section.styles?.galleryBorderColor || '#D041E6'}
                        onChange={(e) => handleSectionStyleChange('galleryBorderColor', e.target.value)}
                        placeholder="#D041E6"
                      />
                    </div>
                  </div>
                  {showColorPicker === 'galleryBorder' && (
                    <div className="color-picker-popover">
                      <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
                      <SketchPicker
                        color={section.styles?.galleryBorderColor || '#D041E6'}
                        onChange={(color) => handleSectionStyleChange('galleryBorderColor', color.hex)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="property-section">
                <div className="section-title"><FaCamera /> รูปภาพ</div>
                
                <div className="property-group">
                <button
                  className="btn-add-gallery-item"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.multiple = false;
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          addGalleryItem(section.id, event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    };
                    input.click();
                  }}
                >
                  <FaPlus /> เพิ่มรูปภาพ
                </button>
              </div>

              {section.styles?.galleryItems && section.styles.galleryItems.length > 0 && (
                <div className="property-group">
                  <label>จัดการรูปภาพ ({section.styles.galleryItems.length} Slots)</label>
                  <div className="gallery-items-list">
                    {section.styles.galleryItems.map((item, index) => (
                      <div key={item.id} className={`gallery-item-row ${!item.imageUrl ? 'empty-slot' : ''} ${selectedGalleryItemId === item.id ? 'selected' : ''}`}>
                        <div className="gallery-item-preview">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={`Slot ${item.slotId}`} />
                          ) : (
                            <div className="no-image"><FaCamera /></div>
                          )}
                        </div>
                        <div className="gallery-item-info">
                          <div className="item-label">
                            Slot {item.slotId} 
                            {!item.imageUrl && <span className="empty-badge">ว่าง</span>}
                          </div>
                          <div className="item-position">
                            {Math.round(item.x)}, {Math.round(item.y)} | {Math.round(item.width)}×{Math.round(item.height)}px
                          </div>
                        </div>
                        <div className="gallery-item-actions">
                          <button
                            className="btn-change-image"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    updateGalleryItem(section.id, item.id, { imageUrl: event.target.result });
                                    setSelectedGalleryItemId(item.id);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              };
                              input.click();
                            }}
                            title={item.imageUrl ? "เปลี่ยนรูป" : "เลือกรูป"}
                          >
                            {item.imageUrl ? <FaSync /> : <FaFolderOpen />}
                          </button>
                          {item.imageUrl && (
                            <>
                              <button
                                className="btn-select-image"
                                onClick={() => setSelectedGalleryItemId(selectedGalleryItemId === item.id ? null : item.id)}
                                title="ปรับแต่งภาพ"
                              >
                                <FaCog />
                              </button>
                              <button
                                className="btn-delete-image"
                                onClick={() => {
                                  updateGalleryItem(section.id, item.id, { imageUrl: null });
                                  if (selectedGalleryItemId === item.id) setSelectedGalleryItemId(null);
                                }}
                                title="ลบรูป"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Adjustment Panel */}
              {selectedGalleryItemId && section.styles?.galleryItems && (
                (() => {
                  const selectedItem = section.styles.galleryItems.find(i => i.id === selectedGalleryItemId);
                  if (!selectedItem || !selectedItem.imageUrl) return null;

                  return (
                    <div className="property-group image-settings-group">
                      <label><FaImage /> ตั้งค่าภาพ - Slot {selectedItem.slotId}</label>
                      
                      <div className="property-subgroup">
                        <label>Object Fit</label>
                        <select
                          value={selectedItem.objectFit || 'cover'}
                          onChange={(e) => updateGalleryItem(section.id, selectedItem.id, { objectFit: e.target.value })}
                        >
                          <option value="cover">Cover (เต็มกรอบ)</option>
                          <option value="contain">Contain (ครบทั้งภาพ)</option>
                          <option value="fill">Fill (ยืดเต็ม)</option>
                          <option value="none">None (ขนาดจริง)</option>
                        </select>
                      </div>

                      <div className="property-subgroup">
                        <label>Zoom: {(selectedItem.zoom || 100)}%</label>
                        <input
                          type="range"
                          min="50"
                          max="200"
                          value={selectedItem.zoom || 100}
                          onChange={(e) => updateGalleryItem(section.id, selectedItem.id, { zoom: parseInt(e.target.value) })}
                          className="zoom-slider"
                        />
                        <div className="slider-labels">
                          <span>50%</span>
                          <span>100%</span>
                          <span>200%</span>
                        </div>
                      </div>

                      <div className="property-subgroup">
                        <label>Position X: {selectedItem.offsetX || 0}%</label>
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          value={selectedItem.offsetX || 0}
                          onChange={(e) => updateGalleryItem(section.id, selectedItem.id, { offsetX: parseInt(e.target.value) })}
                          className="position-slider"
                        />
                      </div>

                      <div className="property-subgroup">
                        <label>Position Y: {selectedItem.offsetY || 0}%</label>
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          value={selectedItem.offsetY || 0}
                          onChange={(e) => updateGalleryItem(section.id, selectedItem.id, { offsetY: parseInt(e.target.value) })}
                          className="position-slider"
                        />
                      </div>

                      <button
                        className="btn-reset-image-settings"
                        onClick={() => {
                          updateGalleryItem(section.id, selectedItem.id, {
                            objectFit: 'cover',
                            zoom: 100,
                            offsetX: 0,
                            offsetY: 0,
                          });
                        }}
                      >
                        <FaSync /> รีเซ็ตค่าเริ่มต้น
                      </button>
                    </div>
                  );
                })()
              )}

              <div className="property-section">
                <div className="section-title"><FaBorderAll /> กรอบรูปภาพ (Frame)</div>
                <div className="frame-styles-grid">
                  {frameStyles.map((style) => (
                    <button
                      key={style.id}
                      className={`frame-style-btn ${selectedGalleryItemId && section.styles?.galleryItems?.find(i => i.id === selectedGalleryItemId)?.frameStyle === style.id ? 'active' : ''}`}
                      onClick={() => {
                        if (selectedGalleryItemId) {
                          // Apply to selected item
                          updateGalleryItem(section.id, selectedGalleryItemId, { frameStyle: style.id });
                        } else {
                          // Apply to all items if no item selected (or maybe add a button for "Apply to All")
                          // For now, let's just apply to all items if no specific item is selected?
                          // Or better, add a specific "Apply to All" button.
                          // Let's follow the user request "Apply to all or some".
                          // If an item is selected, apply to it.
                          // If no item is selected (just section), apply to all?
                          // But currently we can't select section without deselecting item easily in this UI flow if we are in item context.
                          // Actually, if selectedGalleryItemId is null, we are in section context.
                          
                          const newItems = section.styles.galleryItems.map(item => ({
                            ...item,
                            frameStyle: style.id
                          }));
                          handleSectionStyleChange('galleryItems', newItems);
                        }
                      }}
                      title={style.name}
                      style={{
                        ...style.style,
                        width: '40px',
                        height: '40px',
                        margin: '4px',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        background: style.id === 'none' ? '#f0f0f0' : (style.style.background || '#fff'),
                      }}
                    >
                      {style.id === 'none' && <span style={{fontSize: '10px', color: '#999'}}>None</span>}
                    </button>
                  ))}
                </div>
                {selectedGalleryItemId && (
                  <button 
                    className="btn-apply-all"
                    style={{marginTop: '8px', width: '100%', padding: '6px', fontSize: '12px', cursor: 'pointer'}}
                    onClick={() => {
                      const currentItem = section.styles?.galleryItems?.find(i => i.id === selectedGalleryItemId);
                      if (currentItem && currentItem.frameStyle) {
                         const newItems = section.styles.galleryItems.map(item => ({
                            ...item,
                            frameStyle: currentItem.frameStyle
                          }));
                          handleSectionStyleChange('galleryItems', newItems);
                      }
                    }}
                  >
                    ใช้กรอบนี้กับทุกรูป
                  </button>
                )}
              </div>

              <div className="property-group">
                <label>
                  <input
                    type="checkbox"
                    checked={section.styles?.galleryGrid?.enabled ?? true}
                    onChange={(e) => handleSectionStyleChange('galleryGrid', {
                      ...section.styles.galleryGrid,
                      enabled: e.target.checked
                    })}
                  />
                  {' '}เปิดใช้งาน Grid
                </label>
              </div>

              {section.styles?.galleryGrid?.enabled && (
                <>
                  <div className="property-group">
                    <label>ขนาด Grid (px)</label>
                    <input
                      type="number"
                      value={section.styles?.galleryGrid?.size || 20}
                      onChange={(e) => handleSectionStyleChange('galleryGrid', {
                        ...section.styles.galleryGrid,
                        size: parseInt(e.target.value)
                      })}
                      min="10"
                      max="50"
                    />
                  </div>

                  <div className="property-group">
                    <label>ระยะห่าง (Gap) (px)</label>
                    <input
                      type="number"
                      value={section.styles?.galleryGrid?.gap || 20}
                      onChange={(e) => handleSectionStyleChange('galleryGrid', {
                        ...section.styles.galleryGrid,
                        gap: parseInt(e.target.value)
                      })}
                      min="0"
                      max="100"
                    />
                  </div>
                </>
              )}
              </div>
            </>
          )}

          {/* Detail-specific properties */}
          {section.type === sectionTypes.DETAIL && (
            <>
              <div className="property-section">
                <div className="section-title"><FaFileAlt /> รายละเอียด</div>
                
                <div className="property-row">
                  <div className="property-group flex-1">
                    <label><FaFont /> Font</label>
                    <select
                      value={section.styles?.detailFont || 'Sarabun'}
                      onChange={(e) => handleSectionStyleChange('detailFont', e.target.value)}
                    >
                      {fontOptions.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="property-group" style={{width: '90px'}}>
                    <label><FaTextHeight /> Size</label>
                    <input
                      type="number"
                      value={section.styles?.detailSize || 16}
                      onChange={(e) => handleSectionStyleChange('detailSize', parseInt(e.target.value))}
                      min="8"
                      max="72"
                    />
                  </div>
                </div>

                <div className="property-group">
                  <div className="color-group-inline">
                    <label><FaPalette /> สี</label>
                    <div className="color-input-wrapper">
                      <div
                        className="color-preview"
                        style={{ backgroundColor: section.styles?.detailColor || '#414141' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'detailColor' ? null : 'detailColor')}
                      />
                      <input
                        type="text"
                        value={section.styles?.detailColor || '#414141'}
                        onChange={(e) => handleSectionStyleChange('detailColor', e.target.value)}
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                {showColorPicker === 'detailColor' && (
                  <div className="color-picker-popover">
                    <div
                      className="color-picker-cover"
                      onClick={() => setShowColorPicker(null)}
                    />
                    <SketchPicker
                      color={section.styles?.detailColor || '#414141'}
                      onChange={(color) => handleSectionStyleChange('detailColor', color.hex)}
                    />
                  </div>
                )}
              </div>

                <div className="property-row">
                  <div className="property-group flex-1">
                    <label>Align</label>
                    <div className="text-align-buttons">
                      <button
                        className={`align-btn ${(section.styles?.detailAlign || 'left') === 'left' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('detailAlign', 'left')}
                        title="ชิดซ้าย"
                      >
                        <FaAlignLeft />
                      </button>
                      <button
                        className={`align-btn ${(section.styles?.detailAlign || 'left') === 'center' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('detailAlign', 'center')}
                        title="กึ่งกลาง"
                      >
                        <FaAlignCenter />
                      </button>
                      <button
                        className={`align-btn ${(section.styles?.detailAlign || 'left') === 'right' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('detailAlign', 'right')}
                        title="ชิดขวา"
                      >
                        <FaAlignRight />
                      </button>
                      <button
                        className={`align-btn ${(section.styles?.detailAlign || 'left') === 'justify' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('detailAlign', 'justify')}
                        title="เต็มแนว"
                      >
                        <FaAlignJustify />
                      </button>
                    </div>
                  </div>

                  <div className="property-group" style={{width: '90px'}}>
                    <label>Line H.</label>
                    <input
                      type="number"
                      step="0.1"
                      value={section.styles?.detailLineHeight || 1.8}
                      onChange={(e) => handleSectionStyleChange('detailLineHeight', parseFloat(e.target.value))}
                      min="1"
                      max="3"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer-specific properties */}
          {section.type === sectionTypes.FOOTER && (
            <>
              <div className="property-section">
                <div className="section-title"><FaPhone /> ข้อมูลติดต่อ (Footer)</div>
                
                <div className="property-group">
                  <label>ข้อความ</label>
                  <input
                    type="text"
                    value={section.styles?.footerText || '1129 PEA Contact Center'}
                    onChange={(e) => handleSectionStyleChange('footerText', e.target.value)}
                  />
                </div>

                <div className="property-row">
                  <div className="property-group flex-1">
                    <label>จัดตำแหน่ง</label>
                    <div className="text-align-buttons">
                      <button
                        className={`align-btn ${(section.styles?.footerTextAlign || 'center') === 'left' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('footerTextAlign', 'left')}
                        title="ชิดซ้าย"
                      >
                        <FaAlignLeft />
                      </button>
                      <button
                        className={`align-btn ${(section.styles?.footerTextAlign || 'center') === 'center' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('footerTextAlign', 'center')}
                        title="กึ่งกลาง"
                      >
                        <FaAlignCenter />
                      </button>
                      <button
                        className={`align-btn ${(section.styles?.footerTextAlign || 'center') === 'right' ? 'active' : ''}`}
                        onClick={() => handleSectionStyleChange('footerTextAlign', 'right')}
                        title="ชิดขวา"
                      >
                        <FaAlignRight />
                      </button>
                    </div>
                  </div>
                  
                  <div className="property-group" style={{width: '90px'}}>
                    <label>ขนาด</label>
                    <input
                      type="number"
                      value={section.styles?.footerFontSize || 18}
                      onChange={(e) => handleSectionStyleChange('footerFontSize', parseInt(e.target.value))}
                      min="12"
                      max="48"
                    />
                  </div>
                </div>

                <div className="property-group">
                  <div className="color-group-inline">
                    <label><FaPalette /> สีข้อความ</label>
                    <div className="color-input-wrapper">
                      <div
                        className="color-preview"
                        style={{ backgroundColor: section.styles?.footerTextColor || '#FFFFFF' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'footerTextColor' ? null : 'footerTextColor')}
                      />
                      <input
                        type="text"
                        value={section.styles?.footerTextColor || '#FFFFFF'}
                        onChange={(e) => handleSectionStyleChange('footerTextColor', e.target.value)}
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>
                  {showColorPicker === 'footerTextColor' && (
                    <div className="color-picker-popover">
                      <div
                        className="color-picker-cover"
                        onClick={() => setShowColorPicker(null)}
                      />
                      <SketchPicker
                        color={section.styles?.footerTextColor || '#FFFFFF'}
                        onChange={(color) => handleSectionStyleChange('footerTextColor', color.hex)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!block) {
    return (
      <div className={`properties-panel ${showPropertiesPanel ? 'mobile-visible' : ''}`} style={{ width: panelWidth }}>
        {resizeHandle}
        {mobileCloseBtn}
        <div className="properties-header">
          <h3>ตั้งค่าหน้ากระดาษ (Global)</h3>
          <button 
            className="btn-icon" 
            onClick={() => setPanelWidth(panelWidth === 320 ? 450 : 320)}
            title={panelWidth > 350 ? "ย่อ" : "ขยาย"}
          >
            {panelWidth > 350 ? <FaCompressAlt /> : <FaExpandAlt />}
          </button>
        </div>
        
        <div className="properties-content">
          <div className="property-section">
            <div className="section-title"><FaPalette /> พื้นหลัง (Background)</div>
            
            <div className="property-group">
              <div className="bg-type-selector">
                <button 
                  className={`bg-type-btn ${bgType === 'solid' ? 'active' : ''}`}
                  onClick={() => {
                    setBgType('solid');
                    setCanvasGradient(null);
                  }}
                >
                  สีเดียว (Solid)
                </button>
                <button 
                  className={`bg-type-btn ${bgType === 'gradient' ? 'active' : ''}`}
                  onClick={() => setBgType('gradient')}
                >
                  ไล่สี (Gradient)
                </button>
              </div>
            </div>

            {bgType === 'solid' ? (
              <div className="property-group">
                <label>เลือกสีพื้นหลัง</label>
                <div className="color-picker-wrapper">
                  <div
                    className="color-preview"
                    style={{ backgroundColor: canvas.backgroundColor }}
                    onClick={() => setShowColorPicker(showColorPicker === 'canvasBg' ? null : 'canvasBg')}
                  />
                  <input
                    type="text"
                    value={canvas.backgroundColor}
                    onChange={(e) => setCanvasBackground(e.target.value)}
                    placeholder="#FFFFFF"
                  />
                </div>
                {showColorPicker === 'canvasBg' && (
                  <div className="color-picker-popover">
                    <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
                    <SketchPicker
                      color={canvas.backgroundColor}
                      onChange={(color) => setCanvasBackground(color.hex)}
                    />
                  </div>
                )}
              </div>
            ) : (
              <GradientPicker 
                value={canvas.backgroundGradient || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)'}
                onChange={setCanvasGradient}
              />
            )}
          </div>

          <div className="property-section">
            <div className="section-title"><FaFont /> ฟอนต์เริ่มต้น (Global Font)</div>
            <div className="property-group">
              <label>ฟอนต์</label>
              <select
                value={canvas.globalFont || 'Sarabun'}
                onChange={(e) => setGlobalFont(e.target.value)}
              >
                {fontOptions.map((font) => (
                  <option key={font.value} value={font.value}>
                    {font.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="property-section">
            <div className="section-title"><FaInfoCircle /> คำแนะนำ</div>
            <div style={{ padding: '16px' }}>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '20px', 
                fontSize: '13px', 
                color: '#555', 
                lineHeight: '1.6' 
              }}>
                <li>คลิกที่ส่วนต่างๆ ของโปสเตอร์เพื่อแก้ไข</li>
                <li>ลากวางเพื่อจัดตำแหน่ง</li>
                <li>ใช้เมนูด้านซ้ายเพื่อเพิ่มองค์ประกอบ</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleStyleChange = (key, value) => {
    updateBlockStyles(block.id, { [key]: value });
  };

  const renderTextProperties = () => (
    <>
      <div className="property-group">
        <label>ฟอนต์</label>
        <select
          value={block.styles.fontFamily}
          onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
        >
          {fontOptions.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>
      </div>

      <div className="property-group">
        <label>ขนาด</label>
        <input
          type="number"
          value={block.styles.fontSize}
          onChange={(e) => handleStyleChange('fontSize', parseInt(e.target.value))}
          min="8"
          max="200"
        />
      </div>

      <div className="property-group">
        <label>ตกแต่งข้อความ</label>
        <div className="decoration-buttons">
          <button
            className={`decoration-btn ${block.styles.fontWeight === 'bold' ? 'active' : ''}`}
            onClick={() => handleStyleChange('fontWeight', block.styles.fontWeight === 'bold' ? 'normal' : 'bold')}
            title="ตัวหนา"
          >
            <strong>B</strong>
          </button>
          <button
            className={`decoration-btn ${block.styles.fontStyle === 'italic' ? 'active' : ''}`}
            onClick={() => handleStyleChange('fontStyle', block.styles.fontStyle === 'italic' ? 'normal' : 'italic')}
            title="ตัวเอียง"
          >
            <em>I</em>
          </button>
          <button
            className={`decoration-btn ${block.styles.textDecoration === 'underline' ? 'active' : ''}`}
            onClick={() => handleStyleChange('textDecoration', block.styles.textDecoration === 'underline' ? 'none' : 'underline')}
            title="ขีดเส้นใต้"
          >
            <u>U</u>
          </button>
        </div>
      </div>

      <div className="property-group">
        <label>จัดตำแหน่ง</label>
        <div className="align-buttons">
          {textAlignOptions.map((option) => (
            <button
              key={option.value}
              className={`align-btn ${block.styles.textAlign === option.value ? 'active' : ''}`}
              onClick={() => handleStyleChange('textAlign', option.value)}
              title={option.label}
            >
              {option.value === 'left' && <FaAlignLeft />}
              {option.value === 'center' && <FaAlignCenter />}
              {option.value === 'right' && <FaAlignRight />}
              {option.value === 'justify' && <FaAlignJustify />}
            </button>
          ))}
        </div>
      </div>

      <div className="property-group">
        <label>สีข้อความ</label>
        <div className="color-picker-wrapper">
          <div
            className="color-preview"
            style={{ backgroundColor: block.styles.color }}
            onClick={() => setShowColorPicker(showColorPicker === 'text' ? null : 'text')}
          />
          <input
            type="text"
            value={block.styles.color}
            onChange={(e) => handleStyleChange('color', e.target.value)}
            placeholder="#000000"
          />
        </div>
        {showColorPicker === 'text' && (
          <div className="color-picker-popover">
            <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
            <SketchPicker
              color={block.styles.color}
              onChange={(color) => handleStyleChange('color', color.hex)}
            />
          </div>
        )}
      </div>

      <div className="property-group">
        <label>พื้นหลัง</label>
        <select
          value={block.styles.backgroundType || 'solid'}
          onChange={(e) => handleStyleChange('backgroundType', e.target.value)}
        >
          <option value="solid">สีเดียว</option>
          <option value="gradient">ไล่สี</option>
        </select>
      </div>

      {block.styles.backgroundType === 'gradient' ? (
        <div className="property-group">
          <label>ไล่สี (Gradient)</label>
          <GradientPicker
            value={block.styles.backgroundGradient || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)'}
            onChange={(value) => handleStyleChange('backgroundGradient', value)}
          />
        </div>
      ) : (
        <div className="property-group">
          <label>สีพื้นหลัง</label>
          <div className="color-picker-wrapper">
            <div
              className="color-preview"
              style={{ backgroundColor: block.styles.backgroundColor === 'transparent' ? '#ffffff' : block.styles.backgroundColor }}
              onClick={() => setShowColorPicker(showColorPicker === 'bg' ? null : 'bg')}
            />
            <input
              type="text"
              value={block.styles.backgroundColor}
              onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
              placeholder="transparent"
            />
          </div>
          {showColorPicker === 'bg' && (
            <div className="color-picker-popover">
              <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
              <SketchPicker
                color={block.styles.backgroundColor === 'transparent' ? '#ffffff' : block.styles.backgroundColor}
                onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
              />
            </div>
          )}
          <button
            className="transparent-btn"
            onClick={() => handleStyleChange('backgroundColor', 'transparent')}
          >
            ใส
          </button>
        </div>
      )}
    </>
  );

  const renderShapeProperties = () => (
    <>
      <div className="property-group">
        <label>พื้นหลัง</label>
        <select
          value={block.styles.backgroundType || 'solid'}
          onChange={(e) => handleStyleChange('backgroundType', e.target.value)}
        >
          <option value="solid">สีเดียว</option>
          <option value="gradient">ไล่สี</option>
        </select>
      </div>

      {block.styles.backgroundType === 'gradient' ? (
        <div className="property-group">
          <label>ไล่สี (Gradient)</label>
          <GradientPicker
            value={block.styles.backgroundGradient || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)'}
            onChange={(value) => handleStyleChange('backgroundGradient', value)}
          />
        </div>
      ) : (
        <div className="property-group">
          <label>สีพื้นหลัง</label>
          <div className="color-picker-wrapper">
            <div
              className="color-preview"
              style={{ backgroundColor: block.styles.backgroundColor }}
              onClick={() => setShowColorPicker(showColorPicker === 'bg' ? null : 'bg')}
            />
            <input
              type="text"
              value={block.styles.backgroundColor}
              onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
              placeholder="#000000"
            />
          </div>
          {showColorPicker === 'bg' && (
            <div className="color-picker-popover">
              <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
              <SketchPicker
                color={block.styles.backgroundColor}
                onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
              />
            </div>
          )}
        </div>
      )}

      <div className="property-group">
        <label>มุมโค้ง</label>
        <input
          type="range"
          min="0"
          max="100"
          value={block.styles.borderRadius}
          onChange={(e) => handleStyleChange('borderRadius', parseInt(e.target.value))}
        />
        <span>{block.styles.borderRadius}px</span>
      </div>

      <div className="property-group">
        <label>ขนาดขอบ</label>
        <input
          type="number"
          min="0"
          max="20"
          value={block.styles.borderWidth || 0}
          onChange={(e) => handleStyleChange('borderWidth', parseInt(e.target.value))}
        />
      </div>

      {block.styles.borderWidth > 0 && (
        <div className="property-group">
          <label>สีขอบ</label>
          <div className="color-picker-wrapper">
            <div
              className="color-preview"
              style={{ backgroundColor: block.styles.borderColor }}
              onClick={() => setShowColorPicker(showColorPicker === 'border' ? null : 'border')}
            />
            <input
              type="text"
              value={block.styles.borderColor}
              onChange={(e) => handleStyleChange('borderColor', e.target.value)}
            />
          </div>
          {showColorPicker === 'border' && (
            <div className="color-picker-popover">
              <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
              <SketchPicker
                color={block.styles.borderColor}
                onChange={(color) => handleStyleChange('borderColor', color.hex)}
              />
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderImageProperties = () => (
    <>
      <div className="property-group image-settings-group">
        <label><FaImage /> ตั้งค่าภาพ</label>

        <div className="property-subgroup">
          <label><FaBorderAll /> กรอบรูปภาพ (Frame)</label>
          <div className="frame-selector-grid">
            {frameStyles.map((style) => (
              <div
                key={style.id}
                className={`frame-option ${block.styles.frameStyle === style.id ? 'selected' : ''}`}
                onClick={() => handleStyleChange('frameStyle', style.id)}
                title={style.name}
              >
                <div className="frame-preview-box" style={{...style.style, width: '40px', height: '40px', padding: '2px'}}>
                  <div style={{width: '100%', height: '100%', background: '#ccc'}}></div>
                </div>
                <span>{style.name}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="property-subgroup">
          <label>Object Fit</label>
          <select
            value={block.styles.objectFit || 'cover'}
            onChange={(e) => handleStyleChange('objectFit', e.target.value)}
          >
            {imageFitOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="property-subgroup">
          <label>Zoom: {(block.styles.zoom || 100)}%</label>
          <input
            type="range"
            min="50"
            max="200"
            value={block.styles.zoom || 100}
            onChange={(e) => handleStyleChange('zoom', parseInt(e.target.value))}
            className="zoom-slider"
          />
        </div>
      </div>
    </>
  );

  const renderQRCodeProperties = () => (
    <>
      <div className="property-group">
        <label>URL / ข้อความ</label>
        <input
          type="text"
          value={block.styles.value || 'https://www.pea.co.th'}
          onChange={(e) => handleStyleChange('value', e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="property-group">
        <label><FaBorderAll /> กรอบรูปภาพ (Frame)</label>
        <div className="frame-selector-grid">
          {frameStyles.map((style) => (
            <div
              key={style.id}
              className={`frame-option ${block.styles.frameStyle === style.id ? 'selected' : ''}`}
              onClick={() => handleStyleChange('frameStyle', style.id)}
              title={style.name}
            >
              <div className="frame-preview-box" style={{...style.style, width: '40px', height: '40px', padding: '2px'}}>
                <div style={{width: '100%', height: '100%', background: '#ccc'}}></div>
              </div>
              <span>{style.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="property-group">
        <label>สี QR Code</label>
        <div className="color-picker-wrapper">
          <div
            className="color-preview"
            style={{ backgroundColor: block.styles.color || '#000000' }}
            onClick={() => setShowColorPicker(showColorPicker === 'qrColor' ? null : 'qrColor')}
          />
          <input
            type="text"
            value={block.styles.color || '#000000'}
            onChange={(e) => handleStyleChange('color', e.target.value)}
            placeholder="#000000"
          />
        </div>
        {showColorPicker === 'qrColor' && (
          <div className="color-picker-popover">
            <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
            <SketchPicker
              color={block.styles.color || '#000000'}
              onChange={(color) => handleStyleChange('color', color.hex)}
            />
          </div>
        )}
      </div>

      <div className="property-group">
        <label>สีพื้นหลัง</label>
        <div className="color-picker-wrapper">
          <div
            className="color-preview"
            style={{ backgroundColor: block.styles.backgroundColor || '#FFFFFF' }}
            onClick={() => setShowColorPicker(showColorPicker === 'qrBg' ? null : 'qrBg')}
          />
          <input
            type="text"
            value={block.styles.backgroundColor || '#FFFFFF'}
            onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
            placeholder="#FFFFFF"
          />
        </div>
        {showColorPicker === 'qrBg' && (
          <div className="color-picker-popover">
            <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
            <SketchPicker
              color={block.styles.backgroundColor || '#FFFFFF'}
              onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
            />
          </div>
        )}
      </div>
    </>
  );

  const renderLogoProperties = () => (
    <>
      <div className="property-group image-settings-group">
        <label><FaImage /> ตั้งค่า Logo</label>

        <div className="property-subgroup">
          <label><FaBorderAll /> กรอบรูปภาพ (Frame)</label>
          <div className="frame-selector-grid">
            {frameStyles.map((style) => (
              <div
                key={style.id}
                className={`frame-option ${block.styles.frameStyle === style.id ? 'selected' : ''}`}
                onClick={() => handleStyleChange('frameStyle', style.id)}
                title={style.name}
              >
                <div className="frame-preview-box" style={{...style.style, width: '40px', height: '40px', padding: '2px'}}>
                  <div style={{width: '100%', height: '100%', background: '#ccc'}}></div>
                </div>
                <span>{style.name}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="property-subgroup">
          <label>Object Fit</label>
          <select
            value={block.styles.objectFit || 'contain'}
            onChange={(e) => handleStyleChange('objectFit', e.target.value)}
          >
            {imageFitOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="property-subgroup">
          <label>Zoom: {(block.styles.zoom || 100)}%</label>
          <input
            type="range"
            min="50"
            max="200"
            value={block.styles.zoom || 100}
            onChange={(e) => handleStyleChange('zoom', parseInt(e.target.value))}
            className="zoom-slider"
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={`properties-panel ${showPropertiesPanel ? 'mobile-visible' : ''}`} style={{ width: panelWidth }}>
      {resizeHandle}
      {mobileCloseBtn}
      <div className="properties-header">
        <h3>ตั้งค่าวัตถุ (Object)</h3>
        <div className="header-actions">
          <button 
            className="btn-icon" 
            onClick={() => setPanelWidth(panelWidth === 320 ? 450 : 320)}
            title={panelWidth > 350 ? "ย่อ" : "ขยาย"}
          >
            {panelWidth > 350 ? <FaCompressAlt /> : <FaExpandAlt />}
          </button>
        </div>
      </div>

      <div className="properties-content">
        <div className="property-section">
          <div className="section-title"><FaInfoCircle /> ข้อมูลทั่วไป</div>
          <div className="property-group">
            <label>ชนิด (Type)</label>
            <input type="text" value={block.type.toUpperCase()} disabled style={{background: '#f5f5f5', color: '#888'}} />
          </div>
        </div>

        <div className="property-section">
          <div className="section-title"><FaPalette /> ปรับแต่ง</div>
          {block.type === blockTypes.TEXT && renderTextProperties()}
          {block.type === blockTypes.SHAPE && renderShapeProperties()}
          {block.type === blockTypes.IMAGE && renderImageProperties()}
          {block.type === blockTypes.QRCODE && renderQRCodeProperties()}
          {block.type === blockTypes.LOGO && renderLogoProperties()}
        </div>

        <div className="property-section">
          <div className="section-title"><FaMapMarkerAlt /> ตำแหน่งและขนาด</div>
          <div className="property-row">
            <div className="property-group">
              <label>X</label>
              <input
                type="number"
                value={Math.round(block.x)}
                onChange={(e) => updateBlock(block.id, { x: parseInt(e.target.value) })}
              />
            </div>
            <div className="property-group">
              <label>Y</label>
              <input
                type="number"
                value={Math.round(block.y)}
                onChange={(e) => updateBlock(block.id, { y: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="property-row">
            <div className="property-group">
              <label>กว้าง</label>
              <input
                type="number"
                value={Math.round(block.width)}
                onChange={(e) => updateBlock(block.id, { width: parseInt(e.target.value) })}
              />
            </div>
            <div className="property-group">
              <label>สูง</label>
              <input
                type="number"
                value={Math.round(block.height)}
                onChange={(e) => updateBlock(block.id, { height: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="property-group">
            <label>หมุน (องศา)</label>
            <div className="angle-control-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AngleWheel 
                    angle={block.rotation || 0} 
                    onChange={(newAngle) => updateBlock(block.id, { rotation: newAngle })} 
                />
                <input
                    type="number"
                    value={Math.round(block.rotation || 0)}
                    onChange={(e) => updateBlock(block.id, { rotation: parseInt(e.target.value) })}
                    style={{ width: '60px' }}
                />
            </div>
          </div>
        </div>

        <div className="property-section">
          <div className="section-title"><FaLayerGroup /> จัดการ</div>
          <div className="action-buttons">
            <button onClick={() => bringToFront(block.id)} title="นำไปไว้หน้าสุด">
              <FaArrowUp /> หน้าสุด
            </button>
            <button onClick={() => sendToBack(block.id)} title="ส่งไปไว้หลังสุด">
              <FaArrowDown /> หลังสุด
            </button>
            <button onClick={() => duplicateBlock(block.id)} title="ทำซ้ำ">
              <FaCopy /> ทำซ้ำ
            </button>
            <button onClick={() => deleteBlock(block.id)} className="btn-delete" title="ลบ">
              <FaTrash /> ลบ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PropertiesPanel;
